import {Component, OnDestroy, OnInit} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {HhgCarrierRateFilingService} from '../hhg-carrier-rate-filing/hhg-carrier-rate-filing.service';
import {UserService} from '../../services/user/user.service';
import {User} from '../../services/user/user';
import {LoadingService} from '../../services/loading/loading.service';
import {takeWhile} from 'rxjs/operators';
import {AlertService} from '../../services/alert/alert.service';
import {ReportsService} from '../../services/3080-reports/reports.service';
import {ColDef, ColGroupDef, GridOptions} from 'ag-grid-community';
import {Router} from '@angular/router';
import AppConstants from '../../shared/common-components/AppConstants';

@Component({
    selector: 'app-carrier-report',
    templateUrl: './carrier-report.component.html',
    styleUrls: ['./carrier-report.component.css'],
    standalone: false
})
export class CarrierReportComponent implements OnInit, OnDestroy {
  private observer$ = new BehaviorSubject(true);
  currentUser: User | any;
  carrierId: any;
  carrierReports: any;
  carrierReportsOptions: GridOptions = this.initializeGridOptions();
  private gridApi;
  columnDefs: any;
  public overlayNoRowsTemplate;
  rowData: any;
  showExport = false;
  currentYear: any = 0;
  years = [];
  constructor(private hhgrateService: HhgCarrierRateFilingService,
              private userService: UserService,
              private loader: LoadingService,
              private hhgService: HhgCarrierRateFilingService,
              private alertService: AlertService,
              private router: Router,
              private reportsService: ReportsService) {
    this.overlayNoRowsTemplate = '<span style="margin-top: 90px;">No rows to display, please adjust your filters</span>';

  }

  private initializeGridOptions() {
    return <GridOptions>{
      rowSelection: 'multiple',
      defaultColDef: {
        sortable: true,
        resizable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          suppressAndOrCondition: true
        }
      },
      pagination: true,
      paginationPageSize: 25,
      cacheBlockSize: 25,
      rowData: this.carrierReports,
      overlayLoadingTemplate: '<span class="ag-overlay-loading-center">Please wait while your rows are loading</span>',
    };
  }

  /**
   * initializing the ag-grid
   * @param params
   */
  onGridReady(params: any) {
    // params.api.sizeColumnsToFit();
    this.gridApi = params.api;
    this.gridApi.setGroupHeaderHeight(53);
    // this.gridApi.setHeaderHeight(80);
    this.gridApi.setGridOption('headerHeight', 80);
  }

  ngOnInit() {
    this.currentYear = new Date().getFullYear();
    this.years.push({name: this.currentYear, value: this.currentYear});
    for (let i = 1; i < 7; i++) {
      const previousYr = this.currentYear - i;
      this.years.push({name: previousYr, value: previousYr});
    }
    this.onYearChange(this.currentYear);
  }

  onYearChange(year) {
    this.loader.show();
    this.hhgService.getCarrierIdByUser()
      .pipe(takeWhile(() => this.observer$.value))
      .subscribe(carrierId => {
        this.carrierId = carrierId;
        this.reportsService.getCarrierReportsWithYear(this.carrierId, year).pipe(takeWhile(() => this.observer$.value))
          .subscribe(reports => {
            this.carrierReports = reports;
            console.log(this.carrierReports);
            this.rowData = reports;
            this.showExport = true;
            this.loader.hide();
          }, error2 => {
            this.loader.hide();
            this.alertService.error('Error while getting TSP reports');
          });
        this.loader.hide();
      }, (error) => {
        this.loader.hide();
        this.alertService.error('Unable to load TSP info values. Please try again later or contact system administrator');
      });
    this.updateColumnDefs(year);
  }

  updateColumnDefs(year) {
    this.columnDefs = this.getCarrierReportsResultsColumnDefs(year);
  }

  getCarrierReportsResultsColumnDefs(year): (ColDef | ColGroupDef)[] {
    return [
      <ColDef>
          {
            headerName: '',
            children: [
              {
                headerName: 'Bill of Lading Number',
                field: 'bolNumber',
                filter: 'agTextColumnFilter'
              },
              {
                headerName: 'BOL Type',
                field: 'bolType',
                filter: 'agTextColumnFilter'
              },
              {
                headerName: 'Survey Number',
                field: 'surveyNumber',
                filter: 'agTextColumnFilter',
                cellRenderer: params => {
                  if (AppConstants.isNotNullCondition(params)) {
                    if (params.data.surveyStatus === 'Completed' || params.data.surveyStatus === 'Modified') {
                      return '<a tabindex="0">' + params.data.surveyNumber + '</a>';
                    }
                    return params.value;
                  }
                },
                onCellClicked: event => (event.data.surveyStatus === 'Completed' || event.data.surveyStatus === 'Modified') ? this.onSurveyNo(event.data) : null
              },
              {
                headerName: 'Agency Name',
                field: 'agencyName',
                filter: 'agTextColumnFilter'
              },
              {
                headerName: 'Bureau Name',
                field: 'bureauName',
                filter: 'agTextColumnFilter'
              },
              {
                headerName: 'Shipment Type',
                field: 'shipmentType',
                filter: 'agTextColumnFilter'
              },
              {
                headerName: 'Employee Last Name',
                field: 'employeeLastName',
                filter: 'agTextColumnFilter'
              },
              {
                headerName: 'Employee Agency',
                field: 'employeeAgencyName',
                filter: 'agTextColumnFilter'
              },
              {
                headerName: 'Employee Bureau',
                field: 'employeeBureauName',
                filter: 'agTextColumnFilter'
              },
              {
                headerName: 'AMC Contact',
                field: 'amcContact',
                filter: 'agTextColumnFilter'
              },
              {
                headerName: 'FAIC Code',
                field: 'faicCode',
                filter: 'agTextColumnFilter'
              },
              {
                headerName: 'Delivery Date',
                field: 'deliveryDate',
                filter: 'agTextColumnFilter'
              }
            ]
          },
      {
        headerName: 'Employee Response',
        children: this.defineChildColumn(year)
      },
      {
        headerName: 'General Remarks',
        children: [
          {
            headerName: 'Comments Received (Y/N)',
            field: 'comments',
            filter: 'agTextColumnFilter'
          }, {
            headerName: 'Survey Status',
            field: 'surveyStatus',
            filter: 'agTextColumnFilter',
            headerTooltip: 'Held - 3080 Notification to Employee not yet sent (Held until 14 days have expired from the Delivery Date), Pending - Waiting for Employee to complete the 3080, Completed - 3080 Survey completed,Modified - Original 3080 was modified '
          }
        ]
      },
    ];
  }

  defineChildColumn(year){
    return (year>2022)? ( [{
      headerName: 'Quality/Damage',
      field: 'quality',
      width: 100,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'Courtesy',
      field: 'courtesy',
      width: 100,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'Delivery',
      field: 'delivery',
      width: 100,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'Communication',
      field: 'communication',
      width: 150,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'Problems',
      field: 'problems',
      width: 100,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'Recommendation',
      field: 'damage',
      width: 100,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'Over All',
      field: 'overall',
      width: 100,
      filter: 'agTextColumnFilter'
    }]) :([
      {
        headerName: 'Quality',
        field: 'quality',
        width: 100,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Damage',
        field: 'damage',
        width: 100,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Courtesy',
        field: 'courtesy',
        width: 100,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Delivery',
        field: 'delivery',
        width: 100,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Communication',
        field: 'communication',
        width: 150,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Problems',
        field: 'problems',
        width: 100,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Over All',
        field: 'overall',
        width: 100,
        filter: 'agTextColumnFilter'
      }
    ])
  }

  onSurveyNo(data) {
    if (data.surveyStatus === 'Completed' || data.surveyStatus === 'Modified') {
      this.router.navigate(['/evaluationForm'],  { queryParams: { surveyNo: data.surveyNumber } });
    }
  }

  /**
   * Exporting the data in the csv format
   */
  onBtnExport() {
    const date = new Date();
    const params = {
      fileName: 'hhg-survey-carrier-report-' + date + '.csv',
      allColumns: true,
      columnSeparator: ','
    };
    this.gridApi.exportDataAsCsv(params);
    this.showExport = false;
    setTimeout(() => {
      this.showExport = true;
    }, 5200);
  }

  ngOnDestroy(): void {
    this.observer$.next(false);
  }

}
